import posthog from 'posthog-js';
import { getCommonProperties } from './common.helper';

// Track timing of first and last results
let firstResultTime = null;
let lastResultTime = null;
let startTime = null;

// Consent tracking metrics
let consentViewStartTime = null;

// Internal function to reset timing metrics
const resetTimingMetrics = () => {
	firstResultTime = null;
	lastResultTime = null;
	startTime = null;
};

// Internal function to update timing metrics
const updateTimingMetrics = (isFirstResult = false) => {
	const currentTime = Date.now();

	if (!startTime) {
		startTime = currentTime;
	}

	if (isFirstResult && !firstResultTime) {
		firstResultTime = currentTime;
	}

	lastResultTime = currentTime;
};

// Central method to handle analytics enabled check and common functionality
const captureEvent = (event, properties) => {
	if (!global?.sdkOptions?.analyticsEnabled) return;

	posthog.capture(event, {
		...properties,
		...getCommonProperties(),
		entityId: global?.entityId,
	});
};

const captureDiscoveryCompleted = (fipId, metrics) => {
	const isFirstResult = !firstResultTime;
	updateTimingMetrics(isFirstResult);

	captureEvent('fip_discovery_completed', {
		fipId,
		...metrics,
		timeToFirstResult: firstResultTime ? firstResultTime - startTime : null,
		timeToLastResult: lastResultTime ? lastResultTime - startTime : null,
	});
};

const captureDiscoveryError = (fipId, metrics) => {
	const isFirstResult = !firstResultTime;
	updateTimingMetrics(isFirstResult);

	captureEvent('fip_discovery_error', {
		fipId,
		...metrics,
		timeToFirstResult: firstResultTime ? firstResultTime - startTime : null,
		timeToLastResult: lastResultTime ? lastResultTime - startTime : null,
	});
};

const captureDiscoveryRecordNotFound = (fipId, metrics) => {
	const isFirstResult = !firstResultTime;
	updateTimingMetrics(isFirstResult);

	captureEvent('fip_record_not_found', {
		fipId,
		...metrics,
		timeToFirstResult: firstResultTime ? firstResultTime - startTime : null,
		timeToLastResult: lastResultTime ? lastResultTime - startTime : null,
	});
};

const captureAllDiscoveriesCompleted = (metrics) => {
	captureEvent('all_fip_discoveries_completed', {
		...metrics,
		timeToFirstResult: firstResultTime ? firstResultTime - startTime : null,
		timeToLastResult: lastResultTime ? lastResultTime - startTime : null,
	});

	// Reset timing metrics after completion
	resetTimingMetrics();
};

// OTP Analytics Functions
const captureOtpPageLoad = (metrics) => {
	captureEvent('otp_page_loaded', {
		...metrics,
	});
};

const captureOtpInputTriggered = (metrics) => {
	captureEvent('otp_input_triggered', {
		...metrics,
	});
};

const captureOtpInputStart = (metrics) => {
	captureEvent('otp_input_started', {
		...metrics,
	});
};

// OTP Progress Tracking Configuration
const OTP_PROGRESS_CONFIG = {
	trackAtHalfway: true, // Track at 50% completion
	trackAtQuarter: false, // Track at 25% completion
	totalDigits: 6, // Total number of digits in OTP
};

// Helper function to calculate OTP progress metrics
const calculateOtpProgressMetrics = (
	currentLength,
	lastInputTime,
	pageLoadTime,
	correctionCount
) => {
	const currentTime = performance.now();
	const timePerDigit = lastInputTime
		? currentTime - lastInputTime
		: currentTime - pageLoadTime;

	return {
		digitsEntered: currentLength,
		timePerDigit,
		corrections: correctionCount,
		completionPercentage:
			(currentLength / OTP_PROGRESS_CONFIG.totalDigits) * 100,
		timeSinceStart: currentTime - pageLoadTime,
	};
};

// Helper function to check if we should track progress
const shouldTrackOtpProgress = (currentLength) => {
	if (
		OTP_PROGRESS_CONFIG.trackAtHalfway &&
		currentLength === Math.ceil(OTP_PROGRESS_CONFIG.totalDigits / 2)
	) {
		return true;
	}

	if (
		OTP_PROGRESS_CONFIG.trackAtQuarter &&
		currentLength === Math.ceil(OTP_PROGRESS_CONFIG.totalDigits / 4)
	) {
		return true;
	}

	return false;
};

const captureOtpInputProgress = (metrics) => {
	captureEvent('otp_input_progress', {
		...metrics,
	});
};

const captureOtpVerificationAttempt = (metrics) => {
	captureEvent('otp_verification_attempt', {
		...metrics,
	});
};

const captureOtpVerificationResult = (metrics) => {
	captureEvent('otp_verification_result', {
		...metrics,
	});
};
const startConsentView = () => {
	consentViewStartTime = Date.now();
};

const captureConsentApproved = (metrics) => {
	const timeToApprove = consentViewStartTime
		? Date.now() - consentViewStartTime
		: null;

	captureEvent('consent_approved', {
		...metrics,
		timeToApprove,
	});

	// Reset metrics
	consentViewStartTime = null;
};

const captureConsentDenied = (metrics) => {
	const timeToDeny = consentViewStartTime
		? Date.now() - consentViewStartTime
		: null;

	captureEvent('consent_denied', {
		...metrics,
		timeToDeny,
	});

	// Reset metrics
	consentViewStartTime = null;
};

// Track consent page load with account blocking metrics
const captureConsentPageLoaded = (metrics) => {
	captureEvent('consent_page_loaded', {
		...metrics,
	});
};

export {
	captureDiscoveryCompleted,
	captureDiscoveryError,
	captureAllDiscoveriesCompleted,
	captureDiscoveryRecordNotFound,
	captureOtpPageLoad,
	captureOtpInputTriggered,
	captureOtpInputStart,
	captureOtpInputProgress,
	captureOtpVerificationAttempt,
	captureOtpVerificationResult,
	calculateOtpProgressMetrics,
	shouldTrackOtpProgress,
	OTP_PROGRESS_CONFIG,
	startConsentView,
	captureConsentApproved,
	captureConsentDenied,
	captureConsentPageLoaded,
};
